import { useEffect, useRef, useState } from "react";
import {
  useAddSubCategoryMutation,
  useGetSubCategoryByIdQuery,
  useGetAllCategoriesMutation,
  useUpdateSubCategoryMutation,
  useCheckSubCategoryUniqueQuery,
} from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import GenericDropdown from "../../components/GenericDropDown";
import SkeletonAddEditCategoryDialog from "../../skeletonLoading/SkeletonAddEditCategoryDialog";
import {
  MAX_SUB_CATEGORYNAME_LENGTH_MAX,
  MAX_IMAGE_SIZE_MB,
  MAX_IMAGE_SIZE_BYTES,
} from "../../lib/constants";
import useDebounce from "../../lib/utils";
import SortupDialog from "../../components/common/SortupDialog";
import ServiceTypeDropdown from "./ServiceTypeDropDown";

type AddEditSubCategoryDialogProps = {
  subCategoryId?: number;
  isEditOnly: boolean;
  fetchData: () => void;
  setPageNumber: (pageNumber: number) => void;
  setSearchTerm: (searchTerm: string) => void;
  isDialogOpen: boolean;
  setIsDialogOpen: (isDialogOpen: boolean) => void;
  defaultSelectedCategory?: string;
  defaultSelectedServiceType?: string;
};

interface SubCategory {
  subCategoryName: string;
  parentCategoryId: number | undefined;
  imageBlob?: string;
  imageType?: string;
  imageUrl?: string;
}

interface Category {
  id: number;
  name: string;
  isCatering: boolean;
  serviceTypeId: number;
}

export function AddEditSubCategoryDialog({
  subCategoryId,
  isEditOnly,
  fetchData,
  setSearchTerm,
  setPageNumber,
  isDialogOpen,
  setIsDialogOpen,
  defaultSelectedCategory,
  defaultSelectedServiceType,
}: AddEditSubCategoryDialogProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(
    defaultSelectedCategory || ""
  );
  const [selectedServiceType, setSelectedServiceType] = useState<string>(
    defaultSelectedServiceType || ""
  );
  const [subCategoryValues, setSubCategoryValues] = useState<SubCategory>({
    subCategoryName: "",
    parentCategoryId: undefined,
    imageBlob: undefined,
    imageType: undefined,
    imageUrl: undefined,
  });

  const [isUniqueError, setIsUniqueError] = useState(false);
  const toast = useToast();
  const debouncedSubCategorySearchQuery = useDebounce(
    subCategoryValues.subCategoryName,
    500
  );
  const {
    data: isSubCategoryUniqueData,
    isLoading: isSubCategoryUniqueLoading,
  } = useCheckSubCategoryUniqueQuery(
    {
      name: debouncedSubCategorySearchQuery,
      parentCategoryId: subCategoryValues.parentCategoryId || 0,
    },
    {
      skip:
        !debouncedSubCategorySearchQuery ||
        isEditOnly ||
        !subCategoryValues.parentCategoryId,
    }
  );

  const shouldFetchCategory = isDialogOpen && isEditOnly;

  const { data, isLoading, isSuccess } = useGetSubCategoryByIdQuery(
    { subCategoryId: subCategoryId || 0 },
    { skip: !shouldFetchCategory }
  );

  const [AddSubCategory, { isLoading: isSubCategoryLoading }] =
    useAddSubCategoryMutation();

  const [UpdateSubCategory, { isLoading: isUpdateSubCategoryLoading }] =
    useUpdateSubCategoryMutation();

  const [getCategory] = useGetAllCategoriesMutation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (isSuccess && data) {
      setSubCategoryValues({
        subCategoryName: data.data.name,
        parentCategoryId: data.data.parentCategoryId,
        imageUrl: data.data.imageUrl,
      });

      if (categories.length > 0) {
        const parentCategory = categories.find(
          (category) => category.id === data.data.parentCategoryId
        );
        setSelectedServiceType(parentCategory?.serviceTypeId.toString() || "");
        setSelectedCategory(parentCategory?.name || "");
      }
    }
  }, [isSuccess, data, categories, subCategoryId]);

  useEffect(() => {
    if (isSubCategoryUniqueData) {
      setIsUniqueError(!isSubCategoryUniqueData?.data?.isUnique);
    }
  }, [isSubCategoryUniqueData, debouncedSubCategorySearchQuery]);

  const handleSubCategoryNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value.length <= MAX_SUB_CATEGORYNAME_LENGTH_MAX) {
      setSubCategoryValues((prev) => ({
        ...prev,
        subCategoryName: value,
      }));
    } else {
      setSubCategoryValues((prev) => ({
        ...prev,
        subCategoryName: value.slice(0, MAX_SUB_CATEGORYNAME_LENGTH_MAX),
      }));
    }
    setIsUniqueError(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        toast.toast({
          title: "Error!",
          description: `Image size must be less than ${MAX_IMAGE_SIZE_MB}MB.`,
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setSubCategoryValues((prev) => ({
          ...prev,
          imageBlob: reader.result as string,
          imageType: file.type,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddSubCategory = async () => {
    console.log("payload", subCategoryValues);
    try {
      if (isSubCategoryLoading || isUniqueError || isSubCategoryUniqueLoading)
        return;

      if (!subCategoryValues.imageBlob) {
        toast.toast({
          title: "Error!",
          description: "Please upload an image before proceeding.",
        });
        return;
      }

      const response: any = await AddSubCategory({
        body: {
          name: subCategoryValues.subCategoryName,
          parentCategoryId: subCategoryValues.parentCategoryId as number,
          imageBlob: subCategoryValues.imageBlob,
          imageType: subCategoryValues.imageType,
        },
      });

      if (response?.data?.message.includes("success")) {
        setIsDialogOpen(false);
        toast.toast({
          title: "Success!",
          description: "Subcategory added successfully.",
        });
        setPageNumber(1);
        fetchData();
      } else {
        toast.toast({
          title: "Error!",
          description:
            response?.error?.data?.message || "Failed to add subcategory.",
        });
      }
    } catch (error: any) {
      console.error("Error adding subcategory:", error.message);
      toast.toast({
        title: "Error!",
        description: "Failed to add subcategory.",
      });
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getCategory({
        getAllCategoriesRequestDto: { pageSize: 2000 },
      });
      setCategories(response?.data?.data?.categories || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  useEffect(() => {
    if (defaultSelectedCategory) {
      handleCategoryChange(defaultSelectedCategory);
    }
  }, [defaultSelectedCategory, categories]);

  const handleCategoryChange = (value: string) => {
    const newValue = value === "-1" ? "" : value;
    const selectedCat = categories.find((cat) => cat.id === parseInt(newValue));
    setSelectedCategory(selectedCat?.name || "");

    if (newValue === "") {
      setSubCategoryValues((prev) => ({
        ...prev,
        parentCategoryId: undefined,
      }));
    } else if (selectedCat) {
      setSubCategoryValues((prev) => ({
        ...prev,
        parentCategoryId: selectedCat.id,
      }));
    }
  };

  const handleEditSubCategory = async () => {
    if (isUpdateSubCategoryLoading || isUniqueError) return;
    const response = await UpdateSubCategory({
      body: {
        name: subCategoryValues.subCategoryName,
        parentCategoryId: subCategoryValues.parentCategoryId as number,
        id: subCategoryId || 0,
        imageBlob: subCategoryValues.imageBlob,
        imageType: subCategoryValues.imageType,
      },
    });
    if (response?.data?.message.includes("success")) {
      setIsDialogOpen(false);
      toast.toast({
        title: "Success!",
        description: "Subcategory updated successfully.",
      });
      setPageNumber(1);
      fetchData();
    } else {
      toast.toast({
        title: "Error!",
        description: "Failed to update subcategory.",
      });
    }
  };

  const isFormDisabled =
    subCategoryValues.subCategoryName === "" ||
    subCategoryValues.parentCategoryId === undefined ||
    (!isEditOnly && subCategoryValues.imageBlob === undefined) ||
    isSubCategoryLoading ||
    isUpdateSubCategoryLoading ||
    isSubCategoryUniqueLoading ||
    isUniqueError;

  const getFilteredCategories = () => {
    if (
      !selectedServiceType ||
      selectedServiceType === "" ||
      selectedServiceType === "0"
    )
      return categories;

    const filtered = categories.filter((category) => {
      return category.serviceTypeId === parseInt(selectedServiceType);
    });

    return filtered;
  };

  // useEffect(() => {
  //   // Reset the selected category when service type changes
  //   setSelectedCategory("");
  //   setSubCategoryValues((prev) => ({
  //     ...prev,
  //     parentCategoryId: undefined,
  //   }));
  // }, [selectedServiceType]);

  useEffect(() => {
    if (defaultSelectedServiceType) {
      setSelectedServiceType(defaultSelectedServiceType);
    }
  }, [defaultSelectedServiceType]);

  return (
    <>
      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        header={isEditOnly ? "Edit Subcategory" : "Add Subcategory"}
        bodyClassName="w-full min-w-[37vw]"
        footerButtons={[
          {
            name: isEditOnly ? "Update" : "Add",
            onClick: isEditOnly ? handleEditSubCategory : handleAddSubCategory,
            className: isFormDisabled
              ? "opacity-50 cursor-not-allowed text-white"
              : "text-white",
            disabled: isFormDisabled,
          },
          {
            name: "Cancel",
            className: "bg-white text-gray-900 border",
            disabled: isSubCategoryLoading || isUpdateSubCategoryLoading,
            onClick: () => setIsDialogOpen(false),
          },
        ]}
      >
        {isLoading ? (
          <SkeletonAddEditCategoryDialog rows={3} />
        ) : (
          <>
            <label
              htmlFor="categoryName"
              className="block text-sm font-medium text-gray-700"
            >
              Subcategory Name
            </label>
            <input
              type="text"
              id="categoryName"
              value={subCategoryValues.subCategoryName}
              onChange={(e) => handleSubCategoryNameChange(e)}
              className={`focus:outline-customGreenColor mt-1 p-2 border ${
                isUniqueError ? "border-red-500" : "border-gray-300"
              } rounded-md w-full`}
            />
            {isUniqueError && (
              <p className="text-red-500 text-sm mt-1">
                Subcategory name is not unique.
              </p>
            )}
            {isEditOnly ? <br /> : <> </>}
            <span className="text-xs text-gray-500 ">
              {subCategoryValues.subCategoryName.length}/
              {MAX_SUB_CATEGORYNAME_LENGTH_MAX}
            </span>
            <label
              htmlFor="categoryName"
              className="block text-sm mt-4 font-medium text-gray-700"
            >
              Service Type
            </label>
            <ServiceTypeDropdown
              className="w-full bg-white justify-between mt-1 items-center"
              selectedServiceType={selectedServiceType}
              isDisabled={isEditOnly}
              onChange={(value) => {
                setSelectedServiceType(value);
              }}
              dropdownMenuContentWidth="w-[35vw]"
            />
            <label
              htmlFor="isCatering"
              className="block mt-4 text-sm font-medium text-gray-700 mb-1"
            >
              Parent Category
            </label>
            <GenericDropdown
              labelValue={selectedCategory}
              onChange={handleCategoryChange}
              options={getFilteredCategories().map((category) => ({
                value: category.id.toString(),
                label: category.name,
              }))}
              placeholder="Select a category"
              error={false}
              isDisabled={isEditOnly}
              className="w-full justify-between"
              dropdownMenuContentWidth="w-[35vw]"
            />
            <div>
              <label className="block mt-4 text-sm font-medium text-gray-700">
                Subcategory Image
              </label>
              <div className="flex justify-between gap-3">
                <div className="flex items-center space-x-1 relative">
                  <img
                    src={
                      subCategoryValues.imageBlob ||
                      subCategoryValues.imageUrl ||
                      "storeDummyImg.png"
                    }
                    alt="subcategory"
                    className="h-20 w-28 object-contain border border-gray-300 rounded-lg"
                  />
                </div>
                <div
                  className={`mt-2 border-2 border-dashed rounded-md h-40 w-full flex flex-col justify-center items-center p-6 
                        ${
                          dragging
                            ? "border-customGreenColor"
                            : "border-gray-300"
                        }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      // Check file size for drag and drop
                      if (file.size > MAX_IMAGE_SIZE_BYTES) {
                        toast.toast({
                          title: "Error!",
                          description: `Image size must be less than ${MAX_IMAGE_SIZE_MB}MB.`,
                        });
                        return;
                      }
                      handleImageUpload({
                        target: { files: [file] },
                      } as any);
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.gif,.png"
                    onChange={handleImageUpload}
                    ref={fileInputRef}
                    className="hidden"
                  />
                  <p className="text-sm text-neutralDGrey">Drag Image Here</p>
                  <p className="text-sm text-neutralLGrey">OR</p>
                  <p className="text-neutralDGrey cursor-pointer text-sm">
                    Click To Upload
                  </p>
                  <p className="text-xs text-neutralLGrey mt-2">
                    Allowed file extensions: jpg, jpeg, gif, png
                  </p>
                  <p className="text-xs text-neutralLGrey">
                    Maximum file size: {MAX_IMAGE_SIZE_MB}MB
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </SortupDialog>
    </>
  );
}
